import React, { useCallback, useRef, useState } from 'react';
import { SelectProps } from '@cad-arc';
import { debounce } from 'lodash';
import { omitRef } from '../helpers/omit-ref';
import { Option } from '../option';
import type { OnInputChangeParams } from './types';
import composeRefs from '@seznam/compose-react-refs';
import { BaseSelect } from './base-select';
import { useFocusManager } from '@react-aria/focus';
import { moveFocusNext } from '../helpers/move-focus';

type Actions =
    | { type: 'SET_OPTIONS'; options: Option[] }
    | { type: 'SET_LOADING' }
    | { type: 'SET_ERROR'; error: string };
type State =
    | { options: Option[]; loading: false; error: undefined }
    | { options: Option[]; loading: true; error: undefined }
    | { options: Option[]; loading: false; error: string };

type OnChangeHandler = (value: Option<any> | null) => void;

export type GetOptions = React.ComponentProps<typeof AsyncSelect>['getOptions'];
export type AsyncSelectImplementationProps = Omit<
    React.ComponentProps<typeof AsyncSelect>,
    'getOptions'
>;

type AsyncSelectProps = Omit<SelectProps, 'onChange'> & {
    value?: { value: number };
    minimumQueryLength?: number;
    onChange?: OnChangeHandler;
    getOptions: (query: string) => Promise<Option[]>;
    debounceTimer?: number;
    fetchOptionsOnMenuOpen?: boolean;
};

export const AsyncSelect = React.forwardRef<HTMLElement, AsyncSelectProps>(function AsyncSelect(
    props: AsyncSelectProps,
    ref
): React.ReactElement {
    const { value, emptyState, ...rest } = props;
    const [inputHasValue, setInputHasValue] = useState<boolean>(false);
    const [state, dispatch] = React.useReducer(
        (state: State, action: Actions): State => {
            switch (action.type) {
                case 'SET_OPTIONS':
                    return {
                        loading: false,
                        error: undefined,
                        options: action.options,
                    };
                case 'SET_LOADING':
                    return {
                        loading: true,
                        error: undefined,
                        options: [],
                    };
                case 'SET_ERROR':
                    return {
                        loading: false,
                        error: action.error,
                        options: [],
                    };
                default:
                    return state;
            }
        },
        { options: [], loading: false, error: undefined }
    );
    const fetchOptions = React.useMemo(() => {
        return debounce(async (input: string) => {
            if (input.trim().length < (props.minimumQueryLength ?? 0)) {
                return;
            }
            try {
                dispatch({ type: 'SET_LOADING' });
                const options = await props.getOptions(input.trim());
                if (options) {
                    dispatch({
                        type: 'SET_OPTIONS',
                        options,
                    });
                } else {
                    dispatch({ type: 'SET_OPTIONS', options: [] });
                }
            } catch (e) {
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Failed to retrieve user options',
                });
            }
        }, props.debounceTimer ?? 250);
    }, [props]);

    const { options } = state;
    const { onChange } = props;
    const focusManager = useFocusManager();

    const onChangeMemo = React.useMemo(() => {
        return (
            e: Parameters<
                Exclude<React.ComponentProps<typeof BaseSelect>['onChange'], undefined>
            >[0]
        ) => {
            const selectedOption = options.find((option) => option.value === e.target.value);
            onChange?.(selectedOption ?? null);
            if (selectedOption) {
                moveFocusNext(focusManager, selectedOption);
            }
        };
    }, [onChange, focusManager, options]);

    const onInputChangeMemo = React.useMemo(() => {
        return (e: OnInputChangeParams[0], newValue: OnInputChangeParams[1]) => {
            setInputHasValue(!!newValue);
            fetchOptions(newValue);
        };
    }, [fetchOptions]);

    const localRef = useRef<HTMLInputElement | null>(null);

    const composedRef = composeRefs(ref, localRef);

    const onMenuOpen = useCallback(() => {
        fetchOptions(localRef.current?.value ?? '');
    }, [fetchOptions]);

    return (
        <BaseSelect
            filterOption={() => true}
            {...omitRef(rest)}
            // @ts-expect-error ref type mismatch but it works
            ref={composedRef}
            isLoading={state.loading}
            value={value}
            emptyState={inputHasValue ? 'No results found' : emptyState ?? 'Type to search'}
            options={state.options}
            isClearable={value !== null}
            onInputChange={onInputChangeMemo}
            onChange={onChangeMemo}
            onMenuOpen={props.fetchOptionsOnMenuOpen ? onMenuOpen : undefined}
        />
    );
});
